import {
  trigger,
  transition,
  style,
  animate,
  query,
  group,
  AnimationMetadata,
} from '@angular/animations';

/**
 * Route transition animations for swipe navigation
 * Provides smooth slide animations between screens
 */

// Animation timing
const ANIMATION_DURATION = '300ms';
const ANIMATION_EASING = 'ease-out';
const ANIMATION_TIMING = `${ANIMATION_DURATION} ${ANIMATION_EASING}`;

/**
 * Slide animation from right (for forward navigation)
 * Current screen slides out to the left, new screen slides in from right
 */
const slideFromRight: AnimationMetadata[] = [
  // Ensure both pages are positioned correctly
  query(':enter, :leave', [
    style({
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    }),
  ], { optional: true }),
  
  // Set initial positions
  query(':enter', [
    style({
      transform: 'translateX(100%)',
      opacity: 0.8,
    }),
  ], { optional: true }),
  
  // Animate both simultaneously
  group([
    query(':leave', [
      animate(ANIMATION_TIMING, style({
        transform: 'translateX(-30%)',
        opacity: 0.5,
      })),
    ], { optional: true }),
    query(':enter', [
      animate(ANIMATION_TIMING, style({
        transform: 'translateX(0)',
        opacity: 1,
      })),
    ], { optional: true }),
  ]),
];

/**
 * Slide animation from left (for backward navigation)
 * Current screen slides out to the right, new screen slides in from left
 */
const slideFromLeft: AnimationMetadata[] = [
  // Ensure both pages are positioned correctly
  query(':enter, :leave', [
    style({
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    }),
  ], { optional: true }),
  
  // Set initial positions
  query(':enter', [
    style({
      transform: 'translateX(-100%)',
      opacity: 0.8,
    }),
  ], { optional: true }),
  
  // Animate both simultaneously
  group([
    query(':leave', [
      animate(ANIMATION_TIMING, style({
        transform: 'translateX(30%)',
        opacity: 0.5,
      })),
    ], { optional: true }),
    query(':enter', [
      animate(ANIMATION_TIMING, style({
        transform: 'translateX(0)',
        opacity: 1,
      })),
    ], { optional: true }),
  ]),
];

/**
 * Simple fade animation for non-swipe navigation
 */
const fadeAnimation: AnimationMetadata[] = [
  query(':enter, :leave', [
    style({
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    }),
  ], { optional: true }),
  
  query(':enter', [
    style({ opacity: 0 }),
  ], { optional: true }),
  
  group([
    query(':leave', [
      animate('200ms ease-out', style({ opacity: 0 })),
    ], { optional: true }),
    query(':enter', [
      animate('200ms ease-in', style({ opacity: 1 })),
    ], { optional: true }),
  ]),
];

/**
 * Route animations trigger
 * Uses route data 'animation' property or swipe direction to determine animation
 */
export const routeAnimations = trigger('routeAnimations', [
  // Forward transitions (swipe left / navigate next) - index increases
  transition('offers => menus', slideFromRight),
  transition('menus => places', slideFromRight),
  transition('places => liked-offers', slideFromRight),
  
  // Backward transitions (swipe right / navigate previous) - index decreases
  transition('menus => offers', slideFromLeft),
  transition('places => menus', slideFromLeft),
  transition('liked-offers => places', slideFromLeft),
  
  // Skip transitions (e.g., offers to places directly)
  transition('offers => places', slideFromRight),
  transition('offers => liked-offers', slideFromRight),
  transition('menus => liked-offers', slideFromRight),
  
  transition('places => offers', slideFromLeft),
  transition('liked-offers => offers', slideFromLeft),
  transition('liked-offers => menus', slideFromLeft),
  
  // Default transition for other routes
  transition('* <=> *', fadeAnimation),
]);

/**
 * Animation state names for each route
 * These should match the route paths
 */
export const ANIMATION_STATES: Record<string, string> = {
  '/offers': 'offers',
  '/menus': 'menus',
  '/places': 'places',
  '/liked-offers': 'liked-offers',
};

/**
 * Get animation state from route URL
 */
export function getAnimationState(url: string): string {
  const basePath = url.split('?')[0];
  
  // Check exact matches first
  if (ANIMATION_STATES[basePath]) {
    return ANIMATION_STATES[basePath];
  }
  
  // Check prefix matches
  for (const [path, state] of Object.entries(ANIMATION_STATES)) {
    if (basePath.startsWith(path + '/') || basePath === path) {
      return state;
    }
  }
  
  return basePath; // Return URL as fallback
}
